/* eslint-disable import/no-default-export */
/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/ban-types */
/* eslint-disable react/require-default-props */
import { Box } from '@chakra-ui/core';
import React from 'react';
import { FixedSizeList } from 'react-window';
import AutoSizer from 'react-virtualized-auto-sizer';
import { useTable, Column, useSortBy, SortingRule, Row, useFlexLayout } from 'react-table';
import isFunction from 'lodash/isFunction';
import { TableHead, TableHeadProps } from './TableHead';
import { TableRow } from './TableRow';

type DataType = Record<string, unknown>;
// Define interfaces for column and data
type RowStyleFn<T extends DataType> = (params: { index: number; row: Row<T> }) => React.CSSProperties;

interface VirtualTableProps<T extends DataType> {
  data: T[];
  getDataId: (row: T) => React.Key;
  columns: Column<T>[];
  rowHeight?: number;
  tableHeight?: string | number;
  onRowSelect?: (rowData: T) => void;
  selectedRow?: T;
  sortBy?: SortingRule<T>[];
  onSort?: (columnId: string, desc: boolean | undefined) => void;
  pagination?: React.ReactNode;
  emptyTable?: React.ReactNode;
  disableSortReset?: boolean;
  styles?: React.CSSProperties | RowStyleFn<T>;
  tableHeadProps?: Partial<TableHeadProps<T>>;
}

function VirtualTable<T extends DataType>({
  data,
  getDataId,
  columns,
  rowHeight = 50,
  tableHeight,
  onRowSelect,
  selectedRow,
  sortBy: initialSortBy = [],
  onSort,
  pagination,
  emptyTable,
  disableSortReset = false,
  styles,
  tableHeadProps,
}: VirtualTableProps<T>) {
  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = useTable<T>(
    {
      columns,
      data,
      manualSortBy: true,
      disableMultiSort: true,
      disableSortRemove: disableSortReset,
      // defaultColumn,
      initialState: { sortBy: initialSortBy },
    },
    useSortBy,
    useFlexLayout,
  );

  const RenderRow = React.useCallback(
    ({ index, style }: { index: number; style: React.CSSProperties }) => {
      const row: Row<T> = rows[index];
      if (!row) {
        return null;
      }
      prepareRow(row);
      const resolvedStyles = isFunction(styles) ? styles({ index, row }) : styles;
      return (
        <TableRow
          row={row}
          getId={getDataId}
          style={{
            ...style,
            ...resolvedStyles,
          }}
          onSelect={onRowSelect}
          isSelected={selectedRow !== undefined && row.original === selectedRow}
        />
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rows, prepareRow, getDataId, onRowSelect, selectedRow],
  );

  return (
    <Box
      sx={{
        border: '1px solid #e5e5e5',
        borderRadius: '8px',
        height: tableHeight,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box {...getTableProps()} flex={1} display="flex" flexDirection="column">
        <TableHead headerGroups={headerGroups} onSort={onSort} {...tableHeadProps} />
        <Box {...getTableBodyProps()} flex={1}>
          <AutoSizer>
            {({ height, width }: { height: number; width: number }) => (
              <FixedSizeList height={height} itemCount={rows.length} itemSize={rowHeight} width={width}>
                {RenderRow}
              </FixedSizeList>
            )}
          </AutoSizer>
          {!rows?.length && emptyTable && emptyTable}
        </Box>
      </Box>
      {rows.length > 0 && pagination && (
        <Box position="sticky" bottom={0} bg="white" zIndex={1} borderTop="1px solid #e5e5e5" borderBottomRadius="md">
          <Box px={2} py={1}>
            {pagination}
          </Box>
        </Box>
      )}
    </Box>
  );
}

export default VirtualTable;
